import React, { useContext, useState, useEffect } from 'react'
import { View, StyleSheet, Platform } from 'react-native'
import { Text, Button, Datepicker, Select, SelectItem, Icon, Spinner, Card } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { suppliersDictAtom, currencyFormatterAtom, productsDictAtom, storesDictAtom, localeDateServiceAtom, productUnitsDictAtom, userRoleAtom, currencyAtom } from '../../global/AtomsProvider'
import HeaderIconButton from '../../components/HeaderIconButton'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import AdaptiveTableCard from '../../components/AdaptiveTableCard'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { saveNewSupplyOrder, updateSupplyOrder, deleteSupplyOrder, getProductStatsForDays } from '../../global/Firebase/firebase'
import { getProductsBySupplierDict } from '../../utils/productListHelpers'
import { sumProductReportQuantities } from '../../utils/productReportQuantityTotals'
import * as Print from 'expo-print'
import * as Sharing from 'expo-sharing'
import { TouchableOpacity } from 'react-native'
import { useColors } from '../../utils/useColors'
import { SUPPLY_ORDER_STATUSES, resolveStatusOption, statusDisplayText, supplyOrderIsCompleted } from '../../utils/orderStatus'
import { readOrderQuantity, toSavedOrderItems } from '../../utils/supplyOrderQuantity'
import { isFullSupplyOrderLine, supplyOrderFormTotal, supplyOrderLineSum } from '../../utils/supplyOrderLine'
import { initialSupplyOrderDate, readSupplyOrderDate } from '../../utils/supplyOrderDate'
import { normalizeDecimalSeparator } from '../../utils/decimalInput'

// ATL-1103: one shared table, so this screen, SupplyOrdersScreen and the backend's
// COMPLETED_SUPPLY_ORDER_STATUS_IDS cannot drift apart about what a status means.
const ORDER_STATUSES = SUPPLY_ORDER_STATUSES

export default function SupplyOrderDetailScreen({ navigation, route }) {
  const colors = useColors()
  const { t } = useTranslation()
  const [context] = useContext(UserContext)
  const { detail, preselectedSupplier, preselectedStore, itemIndex, sectionIndex, addItemToData, editItemInData, removeItemFromData } = route.params
  
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const productsDict = useAtomValue(productsDictAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const currency = useAtomValue(currencyAtom)

  const [selectedSupplier, setSelectedSupplier] = useState(detail?.supplierID ? suppliersDict[detail.supplierID] : (preselectedSupplier ? suppliersDict[preselectedSupplier.id] : null))
  // ATL-1955: an existing order starts from its own date, or null when it cannot be read — never now.
  const [orderDate, setOrderDate] = useState(() => initialSupplyOrderDate(detail))
  const [items, setItems] = useState(detail?.items || [])
  // ATL-1103 — the seed that made this screen DESTRUCTIVE.
  //
  // `detail?.status || ORDER_STATUSES[0]` put a BARE STRING into this state whenever the read path
  // returned a flattened status, and `handleSave` writes that state straight back to Firestore
  // along with `isCompleted` derived from it. A COMPLETED supply order therefore read as a draft
  // and SAVED as a draft — into Firestore, the copy a read-rollback restores from. Resolving to
  // the canonical option (or keeping an unknown map verbatim) means this state is never a scalar,
  // so no save can flatten one.
  const [status, setStatus] = useState(() => resolveStatusOption(detail?.status, ORDER_STATUSES) ?? ORDER_STATUSES[0])
  const [store, setStore] = useState(detail?.store || (preselectedStore || context.user.store?.id))
  const [isLoading, setIsLoading] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [productsBySuppliers, setProductsBySuppliers] = useState({})
  const [productsSalesData, setProductsSalesData] = useState({})
  const [newSupplyOrderRef, setNewSupplyOrderRef] = useState(null)

  useEffect(() => {
    if (detail?.id) {
      navigation.setOptions({
        headerRight: () => (
          <HeaderIconButton
            onPress={handleDelete}
            iconName={isLoading ? 'loader-outline' : 'trash-outline'}
          />
        ),
      })
    }

    if (!detail) {
      let newProductsBySuppliers = getProductsBySupplierDict(productsDict, true, store)
      setProductsBySuppliers(newProductsBySuppliers)
      if (selectedSupplier) {
        // Add suggested quantities to items
        const itemsWithSuggestions = newProductsBySuppliers[selectedSupplier.id].map(item => ({
          ...item,
          orderQuantity: calculateSuggestedOrderQuantity(item)
        }))
        setItems(itemsWithSuggestions)
      }
    }
  }, [store, selectedSupplier, productsSalesData])

  useEffect(() => {
    if (!detail) {
      getSalesData()
    }
  }, [store])

  const getSalesData = async () => {
    // get sales from daily product stats - 7 days
    var today = new Date()
    var ago = new Date()
    ago.setDate(today.getDate() - 7)
    let newRange = {
      startDate: ago, 
      endDate: today
    }
    const stats = await getProductStatsForDays(context.db, newRange, store)
    setProductsSalesData(sumProductReportQuantities(stats))
  }

  const calculateSuggestedOrderQuantity = (item) => {
    const currentStock = getStockForStore(item, store) || 0
    const weeklySales = productsSalesData[item.id]?.saleQuantity || 0
    const weeklyWriteoffs = productsSalesData[item.id]?.writeOffQuantity || 0
    const threshold = item.stockThreshold || 0

    // Calculate target stock level:
    // 1. Cover next week's expected sales
    // 2. Account for expected writeoffs
    // 3. Maintain minimum threshold
    const targetStock = weeklySales + weeklyWriteoffs + ((threshold || 0) * 2)

    // Order quantity is the difference between target and current stock
    // If current stock is higher than target, suggest 0
    const suggestedQuantity = Math.max(0, targetStock - currentStock)

    // Round up to nearest whole number
    return Math.ceil(suggestedQuantity)
  }

  const handleDelete = async () => {
    if (!isLoading) {
      try {
        setIsLoading(true)
        await deleteSupplyOrder(context.db, detail, detail)
        removeItemFromData?.(sectionIndex, itemIndex)
        setIsLoading(false)
        navigation.goBack()
      } catch(error) {
        console.log(error)
        setSaveError(t('error'))
        Sentry.captureException(error, { tags: { books_write_error: 'supply-order-delete' } })
        setIsLoading(false)
      }
    }
  }

  const handleSave = async () => {
    if (!selectedSupplier) {
      setSaveError(t('supplyOrders.selectSupplier'))
      return
    }

    // ATL-1955: the date field is disabled for an existing order, so a date this screen could not
    // read (a Supabase-read order carries none — ATL-1962) would be saved as today's, over the real
    // one in the Firestore order. Refuse instead.
    const saveDate = readSupplyOrderDate(orderDate)
    if (!saveDate) {
      setSaveError(t('supplyOrders.dateUnreadable'))
      return
    }

    // ATL-1633 / ATL-1966: the lines a save keeps, or a refusal — see `exportableItems`.
    const lines = exportableItems()
    if (!lines) return

    setIsLoading(true)
    setSaveError('')

    const orderData = {
      supplierID: selectedSupplier.id,
      date: saveDate,
      items: lines,
      // The total of the lines SAVED: a negative or zero line is dropped above, so it must not
      // count toward the order's stored total either.
      totalSum: calculateTotalSum(lines),
      status,
      store,
      // ATL-1103: derived, never read off the state object directly. 24 supply orders in prod hold
      // `{id: 'completed', text: '…'}` with NO `isCompleted` key, so `status.isCompleted` is
      // `undefined` for them — and an undefined here is a completed order losing its completion.
      // Falling back to the record's own value means a save can only CONFIRM completion, never
      // blank it.
      isCompleted: supplyOrderIsCompleted(status) ?? detail?.isCompleted ?? false,
      organizationId: context.user.organizationId,
      cashierUID: context.user.uid
    }

    try {
      if (detail?.id) {
        // ATL-1041: `detail` is what this form was seeded from, so the Firestore write names only
        // the fields the save changed.
        await updateSupplyOrder(context.db, detail, orderData, { seededFrom: detail })
        editItemInData?.({...detail, ...orderData}, sectionIndex, itemIndex)
      } else {
        const ref = await saveNewSupplyOrder(context.db, orderData)
        addItemToData?.({...orderData, ref, id: ref.id}, orderData.date, orderData.store)
        setNewSupplyOrderRef(ref)
      }
      setSuccessMessageVisible(true)
      setIsLoading(false)
    } catch (error) {
      console.log(error)
      setSaveError(t('error'))
      Sentry.captureException(error, { tags: { books_write_error: 'supply-order-save' } })
      setIsLoading(false)
    }
  }

  const setItemOrderQuantity = (index, value) => {
    // ATL-1633: the box holds what was TYPED (locale comma → dot) so '1.' and '1.0' survive on the
    // way to '1.05'; the −/+ buttons hand over a number. Nothing is parsed here — `parseFloat`
    // turned '12abc' into a believable 12 — every reader and the save parse strictly instead
    // (utils/supplyOrderQuantity).
    const processedValue = typeof value === 'number' ? value : normalizeDecimalSeparator(value)

    const newItems = [...items]
    newItems[index] = {...newItems[index], orderQuantity: processedValue}
    setItems(newItems)
  }

  // ATL-1955: an order whose date could not be read is exported without one, never as today's.
  const orderDateText = () => (orderDate ? localeDateService.format(orderDate, 'DD MMM YYYY') : '—')

  // ATL-1966: `null` is a sum the lines cannot state (a four-key line) — '—', never a believable 0 ₸.
  const formatFormTotal = (total) => (total === null ? '—' : currencyFormatter.format(total))

  const calculateTotalSum = (lines = items) => {
    return lines.reduce((sum, item) => {
      return sum + (item.purchasePrice || 0) * readOrderQuantity(item.orderQuantity)
    }, 0)
  }

  // ATL-1966: a line without `storeStock` (a four-key line read from Supabase before its backfill)
  // has no stock to show — null, rendered as '—', never 0 and never a crash.
  const getStockForStore = (product, storeId) => {
    if (!storeId) return ''
    const storeStock = product?.storeStock
    if (storeStock === null || typeof storeStock !== 'object') return null
    return storeStock[storeId] || 0
  }

  const fields = [
    {
      key: 'name',
      type: 'text',
      columnStyle: 'nameColumn',
      title: t('supplyOrders.name'),
      textCategory: 's1',
      // ATL-1966: a four-key line names its product `productName`. Display only — it is never saved.
      formatter: (value, item) => value ?? item.productName ?? ''
    },
    {
      key: 'quantity',
      type: 'text',
      title: t('supplyOrders.currentQuantity'),
      textCategory: 'p2',
      formatter: (value, item) => getStockForStore(item, store) ?? '—'
    },
    {
      key: 'weeklySales',
      type: 'text',
      title: t('supplyOrders.weeklySales'),
      textCategory: 'p2',
      formatter: (value, item) => productsSalesData[item.id]?.saleQuantity || 0
    },
    {
      key: 'weeklyWriteoffs',
      type: 'text',
      title: t('supplyOrders.weeklyWriteoffs'),
      textCategory: 'p2',
      formatter: (value, item) => productsSalesData[item.id]?.writeOffQuantity || 0,
      textStyle: value => ({color: value > 0 ? 'red' : 'inherit'})
    },
    {
      key: 'stockThreshold',
      type: 'text',
      title: t('supplyOrders.threshold'),
      textCategory: 'p2',
      formatter: (value, item) => item.stockThreshold || 0
    },
    {
      key: 'orderQuantity',
      type: 'quantity',
      title: t('supplyOrders.orderQuantity'),
      keyboardType: 'decimal-pad',
      caption: item => (productUnitsDict[item.productUnit]?.shortName || t('pcs')),
      onChange: setItemOrderQuantity
    },
    {
      key: 'totalPrice',
      type: 'text',
      columnStyle: 'totalColumn',
      title: t('supplyOrders.totalPrice'),
      textCategory: 's1',
      caption: currency,
      formatter: (value, item) => formatFormTotal(supplyOrderLineSum(item))
    }
  ]

  // ATL-1633: the lines a supplier document lists are exactly the lines a save would keep. An
  // unreadable box refuses the export rather than silently dropping that line from what is sent.
  //
  // ATL-1966: so does a four-key line (an order dual-written before ATL-1966, read from Supabase
  // before its backfill). It has no `orderQuantity`, so `toSavedOrderItems` would DROP it: a save
  // would write the order into Firestore without that line, and an export or a supply made from the
  // order would leave it out.
  const exportableItems = () => {
    if (items.some(item => !isFullSupplyOrderLine(item))) {
      setSaveError(t('supplyOrders.linesUnreadable'))
      return null
    }
    const savedItems = toSavedOrderItems(items)
    if (savedItems.invalid.length > 0) {
      setSaveError(t('notANumberError'))
      return null
    }
    return savedItems.items
  }

  const generatePDF = async () => {
    const lines = exportableItems()
    if (!lines) return
    try {
      // Create HTML content for the PDF
      const htmlContent = `
        <html>
          <head>
            <meta charset="utf-8">
            <title>Supply Order</title>
            <style>
              @media print {
                @page { margin: 0; }
                body { 
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
              body { 
                font-family: Helvetica, Arial, sans-serif;
                padding: 40px;
                max-width: 800px;
                margin: 0 auto;
              }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin-top: 20px;
                margin-bottom: 20px;
              }
              th, td { 
                border: 1px solid #ddd; 
                padding: 12px; 
                text-align: left; 
              }
              th { 
                background-color: #f2f2f2; 
              }
              .header { 
                margin-bottom: 30px;
                padding-bottom: 20px;
                border-bottom: 2px solid #f2f2f2;
              }
              .header h1 {
                margin-bottom: 20px;
              }
              .header p {
                margin: 8px 0;
                color: #333;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${t('supplyOrders.supplyOrder')}</h1>
              <p>${t('date')}: ${orderDateText()}</p>
              <p>${t('supplier')}: ${selectedSupplier?.name || ''}</p>
            </div>

            <table>
              <thead>
                <tr>
                  <th>${t('supplyOrders.name')}</th>
                  <th>${t('supplyOrders.currentQuantity')}</th>
                  <th>${t('supplyOrders.orderQuantity')}</th>
                </tr>
              </thead>
              <tbody>
                ${lines.map(item => `
                  <tr>
                    <td>${item.name}</td>
                    <td>${getStockForStore(item, store) ?? '—'}</td>
                    <td>${item.orderQuantity} ${productUnitsDict[item.productUnit]?.shortName || t('pcs')}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </body>
        </html>
      `;

      if (Platform.OS === 'web') {
        // For web, create a new window and print it
        const printWindow = window.open('', '_blank');
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        // Wait for images to load before printing
        setTimeout(() => {
          printWindow.print();
          // Close the window after print dialog is closed
          setTimeout(() => printWindow.close(), 500);
        }, 250);
      } else {
        // For native platforms, use expo-print
        const { uri } = await Print.printToFileAsync({
          html: htmlContent,
          base64: false
        });

        await Sharing.shareAsync(uri, {
          mimeType: 'application/pdf',
          dialogTitle: t('supplyOrders.exportPDF'),
          UTI: 'com.adobe.pdf'
        });
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      Sentry.captureException(error);
      setSaveError(t('error'));
    }
  };

  const exportAsText = () => {
    const lines = exportableItems()
    if (!lines) return
    try {
      const textContent = [
        `${t('supplyOrders.supplyOrder')}`,
        `${t('supplyOrders.orderDate')}: ${orderDateText()}`,
        `${t('supplier')}: ${selectedSupplier?.name || ''}`,
        `${t('supplyOrders.items')}:`,
        ...lines
          .map(item => 
            ` - ${item.name}: ${item.orderQuantity} ${productUnitsDict[item.productUnit]?.shortName || t('pcs')}`
          ),
        '\n',
      ].join('\n');

      // Copy to clipboard
      if (navigator.clipboard) {
        navigator.clipboard.writeText(textContent);
      }
    } catch (error) {
      console.error('Error exporting as text:', error);
      Sentry.captureException(error);
      setSaveError(t('error'));
    }
  };

  const createSupplyFromOrder = () => {
    // Convert supply order items to supply items format
    // ATL-1633: a supply's quantity moves stock, so it is never handed over as text.
    const lines = exportableItems()
    if (!lines) return
    setSaveError('')
    const supplyItems = lines
      .map(item => ({
        ...item,
        // Use orderQuantity as supply quantity
        quantity: item.orderQuantity,
      }));

    navigation.navigate('SupplyDetail', {
      // Pass supplier info
      preselectedSupplier: selectedSupplier,
      // Pass formatted items
      preselectedItems: supplyItems,
      // Pass store info
      preselectedStore: store,
      // Pass supply order reference for tracking
      supplyOrderRef: detail?.ref || newSupplyOrderRef,
    });
  };

  const renderFooter = () => (
    <View style={styles.footer}>
      <Text category='s1' style={styles.totalSum}>
        {t('supplyOrders.totalSum') + ': ' + formatFormTotal(supplyOrderFormTotal(items))}
      </Text>

      {saveError ? (
        <Text category='p1' status='danger' style={styles.errorText}>
          {saveError}
        </Text>
      ) : null}

      {successMessageVisible ? (
        <>
          <Text status='success'>{t('success')}</Text>
          <Button appearance='outline' onPress={() => navigation.goBack()} style={styles.button}>
            {t('back')}
          </Button>
        </>
      ) : (
        <>
          {isLoading ? (
            <View style={{justifyContent: 'center', alignItems: 'center'}}><Spinner /></View>
          ) : (
            <Button style={styles.button} onPress={handleSave}>
              {t('save')}
            </Button>
          )}
        </>
      )}
      { (detail?.id || successMessageVisible) && (   
        <>
          <Button appearance='outline' style={styles.button} onPress={generatePDF}>
            {t('supplyOrders.exportPDF')}
          </Button>
          <Button appearance='outline' style={styles.button} onPress={exportAsText}>
            {t('supplyOrders.exportText')} 
          </Button>
          <Button 
            appearance='outline' 
            style={styles.button} 
            onPress={createSupplyFromOrder}
          >
            {t('supplyOrders.createSupply')}
          </Button>
        </>
      )}
      <View style={{height: 100}} />
    </View>
  )

  return (
    <KeyboardAwareScrollView style={styles.container}>
      <Text category='label' style={styles.label}>
        {t('supplier')}
      </Text>
      <PlatformDependantSelect
        data={Object.values(suppliersDict)}
        selectedOption={selectedSupplier}
        disabled={detail != null}
        onSelect={setSelectedSupplier}
        withSearch={true}
      />

      { selectedSupplier && (
        <>
          { Object.values(storesDict).length > 1 && (userRole?.permissions?.['viewOtherStores'] || context.user.role === 'admin') && (
            <>
              <Text category='label' style={styles.label}>
                {t('store')}
              </Text>
              <PlatformDependantSelect
                data={Object.values(storesDict)}
                selectedOption={storesDict?.[store]}
                disabled={detail != null}
                onSelect={(itemValue, itemIndex) => {
                  setStore(itemValue.id)
                }}
              />
            </>
          )}

          <Text category='label' style={styles.label}>
            {t('date')}
          </Text>
          <Datepicker
            placement='top'
            date={orderDate}
            onSelect={setOrderDate}
            dateService={localeDateService}
            min={new Date('2020-12-01')}
            max={new Date('2029-12-31')}
            disabled={detail != null}
          />

          <Text category='label' style={styles.label}>
            {t('supplyOrders.status')}
          </Text>
          <Select
            value={statusDisplayText(status, ORDER_STATUSES, t) ?? ''}
            onSelect={index => setStatus(ORDER_STATUSES[index.row])}
          >
            {ORDER_STATUSES.map(status => 
              <SelectItem key={status.id} title={t(status.text)} />
            )}
          </Select>

          {detail?.supplyId && (
            <Card status='basic' style={{marginTop: 15}}>
              <TouchableOpacity onPress={() => navigation.navigate('SupplyDetail', {detailId: detail.supplyId})}>
                <View style={{flexDirection: 'row', alignItems: 'flex-end'}}>
                  <Text style={{color: colors.primary}}>{t('supplies.supply')}</Text>
                  <Icon name='external-link-outline' width={16} height={16} fill={colors.primary} style={{marginLeft: 4}} /> 
                </View>
              </TouchableOpacity>
              <View>
                <Text>{t('date')}: {localeDateService.format(detail.supplyDate?.toDate?.() || new Date(), 'DD MMM YYYY')}</Text>
                <Text>{t('supplyOrders.totalSum')}: {currencyFormatter.format(detail.totalSum || 0)}</Text>
              </View>
            </Card>
          )}

          <Text category='label' style={styles.label}>
            {t('supplyOrders.items')}
          </Text>
          <AdaptiveTableCard
            data={items}
            fields={fields}
            disabled={detail != null}
            breakpoint={800}
            renderFooter={renderFooter}
          />
        </>
      )}
    </KeyboardAwareScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  footer: {
    marginTop: 20,
    marginBottom: 60,
  },
  button: {
    marginVertical: 10,
  },
  errorText: {
    marginBottom: 10,
  },
  totalSum: {
    marginBottom: 16,
  }
})
